import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  MEDIA_SCHEMA_VERSION,
  type MediaReference,
  type MediaUploadContext,
  type MediaUploadProvider,
} from '@mtng-reports/models';
import { firstValueFrom } from 'rxjs';
import { filter, map, timeout } from 'rxjs/operators';
import { trackAnalyticsEvent } from '../shared/analytics';
import { UploadCheckpointService } from './upload-checkpoint.service';

export type MediaUploadSettings = {
  selectedProvider: MediaUploadProvider;
  effectiveProvider: MediaUploadProvider;
};

export type UploadedMedia = MediaReference & {
  source_url: string;
  resolvedUrl?: string;
  resolvedUrlExpiresAt?: number;
};

type PreparedUpload = {
  mediaId: string;
  uploadUrl: string;
  headers: Record<string, string>;
};

type ResolvedMediaUrl = {
  url: string;
  expiresAt?: number;
};

const SIGNED_URL_REFRESH_BUFFER_MS = 30_000;
const UPLOAD_IDLE_TIMEOUT_MS = 60_000;
const MEDIA_REFRESH_RETRY_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class MediaUploadService {
  constructor(
    private functions: Functions,
    private http: HttpClient,
    private uploadCheckpoint: UploadCheckpointService,
  ) {}

  async getSettings(): Promise<MediaUploadSettings> {
    const callable = httpsCallable<Record<string, never>, MediaUploadSettings>(
      this.functions,
      'getMediaUploadSettings',
    );
    return (await callable({})).data;
  }

  async updateProvider(provider: MediaUploadProvider): Promise<MediaUploadSettings> {
    const callable = httpsCallable<{ provider: MediaUploadProvider }, MediaUploadSettings>(
      this.functions,
      'updateMediaUploadSettings',
    );
    return (await callable({ provider })).data;
  }

  async upload(
    dataUrl: string,
    filename: string,
    context: MediaUploadContext,
    progress?: (value: number) => void,
  ): Promise<UploadedMedia> {
    const startedAt = Date.now();
    const ownsCheckpoint = context.uploadAttemptId === undefined;
    const uploadAttemptId = context.uploadAttemptId ?? this.uploadCheckpoint.start({});
    let provider: MediaUploadProvider | 'unknown' = 'unknown';

    try {
      const settings = await this.getSettings();
      provider = settings.effectiveProvider;
      const media = await this.uploadR2(dataUrl, filename, context, uploadAttemptId, progress);

      if (ownsCheckpoint) this.uploadCheckpoint.finish(uploadAttemptId, 'success');
      trackAnalyticsEvent('image_upload_success', {
        provider,
        duration_ms: Date.now() - startedAt,
      });
      return media;
    } catch (error) {
      if (ownsCheckpoint) this.uploadCheckpoint.finish(uploadAttemptId, 'failure');
      trackAnalyticsEvent('image_upload_error', {
        provider,
        duration_ms: Date.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  async hydrateContentMedia(value: unknown, now = Date.now()): Promise<unknown> {
    if (Array.isArray(value)) {
      return Promise.all(value.map((item) => this.hydrateContentMedia(item, now)));
    }

    if (!value || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
      return value;
    }

    const record = value as Record<string, unknown>;
    if (record.provider === 'r2' && typeof record.id === 'string' && !record.sourceUrl) {
      const currentUrl = typeof record.resolvedUrl === 'string' ? record.resolvedUrl : undefined;
      const expiresAt = typeof record.resolvedUrlExpiresAt === 'number' ? record.resolvedUrlExpiresAt : undefined;
      if (currentUrl && expiresAt && expiresAt - SIGNED_URL_REFRESH_BUFFER_MS > now) {
        return record;
      }

      const resolved = await this.resolve(record.id, now);
      const previousUrl = currentUrl ?? record.source_url;
      return {
        ...record,
        ...(!record.image || record.image === previousUrl ? { image: resolved.url } : {}),
        ...(!record.thumbnail || record.thumbnail === previousUrl ? { thumbnail: resolved.url } : {}),
        source_url: resolved.url,
        resolvedUrl: resolved.url,
        resolvedUrlExpiresAt: resolved.expiresAt,
      };
    }

    const entries = await Promise.all(
      Object.entries(record).map(async ([key, entryValue]) => [
        key,
        await this.hydrateContentMedia(entryValue, now),
      ] as const),
    );
    return Object.fromEntries(entries);
  }

  startContentMediaRefresh(
    getContent: () => unknown,
    updateContent: (content: unknown) => void,
  ): () => void {
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const schedule = (content: unknown) => {
      const delay = this.getContentMediaRefreshDelay(content) ?? MEDIA_REFRESH_RETRY_MS;
      if (cancelled) return;

      refreshTimer = setTimeout(() => void refresh(), delay);
    };
    const retry = () => {
      if (cancelled) return;
      refreshTimer = setTimeout(() => void refresh(), MEDIA_REFRESH_RETRY_MS);
    };
    const refresh = async () => {
      try {
        const content = getContent();
        if (!this.hasPrivateContentMedia(content)) {
          schedule(content);
          return;
        }

        const hydrated = await this.hydrateContentMedia(content);
        if (cancelled) return;
        updateContent(hydrated);
        schedule(hydrated);
      } catch (error) {
        console.error('Failed to refresh private report media', error);
        retry();
      }
    };

    void refresh();

    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }

  private getContentMediaRefreshDelay(value: unknown, now = Date.now()): number | undefined {
    const expirations: number[] = [];
    const collect = (entry: unknown) => {
      if (Array.isArray(entry)) {
        entry.forEach(collect);
        return;
      }
      if (!entry || typeof entry !== 'object' || Object.getPrototypeOf(entry) !== Object.prototype) return;

      const record = entry as Record<string, unknown>;
      if (
        record.provider === 'r2'
        && !record.sourceUrl
        && typeof record.resolvedUrlExpiresAt === 'number'
      ) {
        expirations.push(record.resolvedUrlExpiresAt);
      }
      Object.values(record).forEach(collect);
    };
    collect(value);
    if (expirations.length === 0) return undefined;

    return Math.max(0, Math.min(...expirations) - SIGNED_URL_REFRESH_BUFFER_MS - now);
  }

  private hasPrivateContentMedia(value: unknown): boolean {
    if (Array.isArray(value)) return value.some((entry) => this.hasPrivateContentMedia(entry));
    if (!value || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) return false;

    const record = value as Record<string, unknown>;
    if (record.provider === 'r2' && !record.sourceUrl && typeof record.id === 'string') return true;
    return Object.values(record).some((entry) => this.hasPrivateContentMedia(entry));
  }

  private parseDataUrl(dataUrl: string): { base64: string; contentType: string; blob: Blob } {
    const match = dataUrl.match(/^data:([^;,]+);base64,(.+)$/);
    const contentType = match?.[1] ?? 'image/webp';
    const base64 = match?.[2] ?? dataUrl;

    if (!base64) throw new Error('Invalid image data URL');

    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));

    return { base64, contentType, blob: new Blob([bytes], { type: contentType }) };
  }

  private async uploadR2(
    dataUrl: string,
    filename: string,
    context: MediaUploadContext,
    uploadAttemptId: string,
    progress?: (value: number) => void,
  ): Promise<UploadedMedia> {
    const { blob, contentType } = this.parseDataUrl(dataUrl);
    const prepare = httpsCallable<{
      fileName: string;
      contentType: string;
      size: number;
      visibility: 'published';
      mediaSchemaVersion: number;
      companyId: string;
      reportId?: string;
      uploadAttemptId: string;
    }, PreparedUpload>(this.functions, 'prepareMediaUpload');
    this.uploadCheckpoint.advance(uploadAttemptId, 'r2_prepare_started', {
      provider: 'r2',
      fileSizeBytes: blob.size,
    });
    const prepared = (await prepare({
      fileName: filename,
      contentType,
      size: blob.size,
      visibility: 'published',
      mediaSchemaVersion: MEDIA_SCHEMA_VERSION,
      ...context,
      uploadAttemptId,
    })).data;
    const request = new HttpRequest('PUT', prepared.uploadUrl, blob, {
      headers: new HttpHeaders(prepared.headers),
      reportProgress: true,
    });
    const finalize = httpsCallable<{ mediaId: string }, MediaReference>(
      this.functions,
      'finalizeMediaUpload',
    );
    const finalizeUpload = async (): Promise<MediaReference> => {
      this.uploadCheckpoint.advance(uploadAttemptId, 'r2_finalize_started', { provider: 'r2' });
      return (await finalize({ mediaId: prepared.mediaId })).data;
    };
    let media: MediaReference | undefined;

    try {
      this.uploadCheckpoint.advance(uploadAttemptId, 'r2_put_started', { provider: 'r2' });
      await firstValueFrom(this.http.request(request).pipe(
        // Unsubscribe aborts the PUT before retrying; progress keeps slow uploads alive.
        timeout({ each: UPLOAD_IDLE_TIMEOUT_MS }),
        map((event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            progress?.(Math.round((event.loaded / event.total) * 100));
          }
          return event;
        }),
        filter((event) => event.type === HttpEventType.Response),
      ));
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 412) {
        try {
          media = await finalizeUpload();
        } catch {
          throw this.toR2PutError(error);
        }
      }
    }

    media ??= await finalizeUpload();
    const resolved = media.sourceUrl
      ? { url: media.sourceUrl }
      : await this.resolve(media.id);
    progress?.(100);

    return {
      ...media,
      source_url: resolved.url,
      ...(!media.sourceUrl
        ? { resolvedUrl: resolved.url, resolvedUrlExpiresAt: resolved.expiresAt }
        : {}),
    };
  }

  private toR2PutError(error: unknown): Error {
    // Pages log upload failures with String(error); a raw HttpErrorResponse
    // stringifies to "[object Object]" and hides the failing step.
    if (error instanceof Error && !(error instanceof HttpErrorResponse)) {
      return error;
    }

    const message = error instanceof HttpErrorResponse
      ? `R2 upload failed: HTTP ${error.status} ${error.statusText} for signed PUT`
      : `R2 upload failed: ${String(error)}`;
    const wrapped = new Error(message) as Error & { cause?: unknown };
    wrapped.cause = error;
    return wrapped;
  }

  private async resolve(mediaId: string, now = Date.now()): Promise<ResolvedMediaUrl> {
    const callable = httpsCallable<{ mediaId: string }, { url: string; expiresInSeconds?: number }>(
      this.functions,
      'resolveMedia',
    );
    const resolved = (await callable({ mediaId })).data;
    return {
      url: resolved.url,
      ...(resolved.expiresInSeconds
        ? { expiresAt: now + resolved.expiresInSeconds * 1000 }
        : {}),
    };
  }
}
